import React from 'react';
import { Button } from './ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './ui/tooltip';
import { ExternalLink, Trash2 } from 'lucide-react';

/**
 * Tooltip-wrapped control name + description block.
 * Shows full text instantly on hover when the visible text is truncated.
 */
export const ControlNameWithTooltip = ({ control, nameClassName, descriptionClassName, children }) => (
  <TooltipProvider delayDuration={0}>
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="cursor-default min-w-0">
          <h4 className={nameClassName || 'font-medium text-slate-900 truncate'}>
            {control.control_name}
          </h4>
          {children /* extra inline badges */}
          {control.description && (
            <p className={descriptionClassName || 'text-sm text-slate-600 line-clamp-1'}>
              {control.description}
            </p>
          )}
        </div>
      </TooltipTrigger>
      <TooltipContent
        side="bottom"
        align="start"
        className="max-w-md bg-slate-900 text-white p-3 text-xs leading-relaxed"
      >
        <div className="font-semibold mb-1">{control.control_name}</div>
        {control.description && (
          <div className="text-slate-200 whitespace-pre-wrap">{control.description}</div>
        )}
      </TooltipContent>
    </Tooltip>
  </TooltipProvider>
);

/**
 * Compact icon-only View + Delete action cluster with instant tooltips.
 */
export const ControlRowActions = ({ control, onView, onDelete }) => (
  <TooltipProvider delayDuration={0}>
    <div className="flex items-center gap-1">
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            onClick={onView}
            className="h-8 w-8 text-slate-600 hover:text-navy-700 hover:bg-slate-100"
            data-testid={`view-control-${control.id}`}
          >
            <ExternalLink className="w-4 h-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent className="bg-slate-900 text-white text-xs">View details</TooltipContent>
      </Tooltip>
      {!control.is_deleted && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              onClick={onDelete}
              className="h-8 w-8 text-red-600 hover:text-red-700 hover:bg-red-50"
            >
              <Trash2 className="w-4 h-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent className="bg-slate-900 text-white text-xs">Delete</TooltipContent>
        </Tooltip>
      )}
    </div>
  </TooltipProvider>
);
